import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { COMPANY, SECTORS, DIVISIONS } from "../data/content";

const About = () => {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About Worldly Solutions"
        title={<>A trusted partner in <em className="text-brand-green font-serif italic">management consulting.</em></>}
        subtitle={COMPANY.mission + " With strategic insights, innovative solutions, and unparalleled expertise, we empower organizations to overcome challenges, seize opportunities, and achieve sustainable growth."}
      />

      {/* Company Story */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">Who we are</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-brand-ink leading-tight">
              We help organizations <em className="text-brand-green font-serif italic">thrive</em> — in dynamic, competitive landscapes.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-brand-slate text-lg leading-relaxed">
            <p>Worldly Solutions Private Limited specializes in helping organizations across industries optimize their operations, enhance their performance, and achieve sustainable growth.</p>
            <p>Our seasoned consultants bring together diverse industry experience and deep functional knowledge, delivering tailored solutions that drive measurable results — rooted in deep industry knowledge, extensive research, and a commitment to understanding our clients' specific needs.</p>
            <p className="text-brand-ink font-medium">We combine cutting-edge methodologies with practical insights — to deliver results that drive long-term success.</p>
          </div>
        </div>
      </section>

      {/* Our Approach - 3 step */}
      <section className="py-24 bg-brand-green-50/50">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4 text-center">Our approach</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-brand-ink leading-tight text-center max-w-3xl mx-auto">A holistic approach to management consulting.</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            {[
              { n: "01", t: "Collaborate", d: "We work closely with clients to understand unique needs, goals and aspirations — building trust from day one." },
              { n: "02", t: "Diagnose & Design", d: "Diverse industry experience and deep functional knowledge inform tailored, defensible solutions." },
              { n: "03", t: "Execute & Measure", d: "Practical, results-driven recommendations — implemented, monitored and continuously improved." },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-black/5 p-8 rounded-sm hover:shadow-card-hover transition-shadow">
                <div className="font-serif text-5xl text-brand-gold">{s.n}</div>
                <h3 className="font-serif text-2xl text-brand-ink mt-4">{s.t}</h3>
                <p className="text-brand-slate mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divisions */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">Our divisions</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-brand-ink leading-tight max-w-3xl">Specialist depth across the disciplines that matter most.</h2>

          <div className="mt-16 space-y-20">
            {DIVISIONS.map((d, i) => (
              <div key={d.title} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="lg:col-span-6">
                  <div className="relative aspect-[5/4] rounded-sm overflow-hidden shadow-glass">
                    <img src={d.img} alt={d.title} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <div className="text-xs tracking-[0.25em] uppercase text-brand-gold mb-3">Division 0{i + 1}</div>
                  <h3 className="font-serif text-3xl md:text-4xl text-brand-ink tracking-tight">{d.title}</h3>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {d.items.map(item => (
                      <li key={item} className="flex items-start gap-2 text-brand-slate"><CheckCircle2 className="h-4 w-4 text-brand-green mt-1 flex-shrink-0" />{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Work Experience - Sectors */}
      <section data-testid="sectors-section" className="py-24 md:py-32 bg-brand-ink text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-brand-green/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">Our work experience</div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] max-w-3xl">Sectors where our work has <em className="text-brand-gold font-serif italic">moved the needle</em>.</h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SECTORS.map((s, i) => (
              <div key={s} data-testid={`sector-${i}`} className="border border-white/10 bg-white/5 backdrop-blur-sm p-7 rounded-sm hover:bg-white/10 transition-colors group">
                <div className="text-xs tracking-widest text-brand-gold">SECTOR · 0{i + 1}</div>
                <div className="font-serif text-xl mt-3 leading-tight">{s}</div>
                <ArrowRight className="h-4 w-4 mt-6 text-white/40 group-hover:text-brand-gold group-hover:translate-x-1 transition-all" />
              </div>
            ))}
          </div>

          <div className="mt-16">
            <Link to="/contact" data-testid="about-cta" className="inline-flex items-center gap-3 bg-brand-gold text-brand-ink px-7 py-4 rounded-sm font-semibold hover:bg-white transition-colors">
              Discuss your engagement <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
